import { useState, useCallback, createContext, useContext, useRef, ReactNode } from 'react';
import ConfirmDialog from '../components/ConfirmDialog';
import type { DialogState, ConfirmFunction } from '../types';

interface ConfirmProviderProps {
  children: ReactNode;
}

const ConfirmContext = createContext<ConfirmFunction | null>(null);

/**
 * Provider component that wraps the app to enable useConfirm hook
 */
export function ConfirmProvider({ children }: ConfirmProviderProps) {
    // Store the element that triggered the dialog for focus restoration
    const triggerElementRef = useRef<Element | null>(null);

    const [dialogState, setDialogState] = useState<DialogState>({
        isOpen: false,
        title: '',
        message: '',
        confirmText: 'Confirmer',
        cancelText: 'Annuler',
        variant: 'danger',
        onConfirm: () => {},
    });

    const confirm = useCallback<ConfirmFunction>(({ title, message, confirmText, cancelText, variant = 'danger' }) => {
        // Store the currently focused element before opening dialog
        triggerElementRef.current = document.activeElement;

        return new Promise<boolean>((resolve) => {
            setDialogState({
                isOpen: true,
                title,
                message,
                confirmText: confirmText || 'Confirmer',
                cancelText: cancelText || 'Annuler',
                variant,
                onConfirm: () => resolve(true),
                onCancel: () => resolve(false),
            });
        });
    }, []);

    // Restore focus to trigger element after dialog closes
    const restoreFocus = useCallback(() => {
        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
            if (triggerElementRef.current && 'focus' in triggerElementRef.current && typeof (triggerElementRef.current as HTMLElement).focus === 'function') {
                (triggerElementRef.current as HTMLElement).focus();
            }
            triggerElementRef.current = null;
        });
    }, []);

    const handleClose = useCallback(() => {
        dialogState.onCancel?.();
        setDialogState((prev) => ({ ...prev, isOpen: false }));
        restoreFocus();
    }, [dialogState, restoreFocus]);

    const handleConfirm = useCallback(() => {
        dialogState.onConfirm?.();
        setDialogState((prev) => ({ ...prev, isOpen: false }));
        restoreFocus();
    }, [dialogState, restoreFocus]);

    return (
        <ConfirmContext.Provider value={confirm}>
            {children}
            <ConfirmDialog
                isOpen={dialogState.isOpen}
                onClose={handleClose}
                onConfirm={handleConfirm}
                title={dialogState.title}
                message={dialogState.message}
                confirmText={dialogState.confirmText}
                cancelText={dialogState.cancelText}
                variant={dialogState.variant}
            />
        </ConfirmContext.Provider>
    );
}

/**
 * Hook to show confirmation dialogs
 *
 * @example
 * const confirm = useConfirm();
 * const handleDelete = async () => {
 *   const confirmed = await confirm({
 *     title: 'Supprimer?',
 *     message: 'Cette action est irréversible.',
 *     variant: 'danger'
 *   });
 *   if (confirmed) {
 *     // proceed with deletion
 *   }
 * };
 */
export function useConfirm(): ConfirmFunction {
    const context = useContext(ConfirmContext);
    if (!context) {
        throw new Error('useConfirm must be used within a ConfirmProvider');
    }
    return context;
}
